import { useCallback, useEffect, useRef, useState } from 'react'

function normalizeCurrentTime(seconds: number) {
  return Number.isFinite(seconds) && seconds > 0 ? Math.floor(seconds) : 0
}

function normalizeDuration(seconds: number) {
  return Number.isFinite(seconds) && seconds > 0 ? Math.round(seconds) : 0
}

export function formatAudioTime(seconds: number) {
  const normalized = normalizeCurrentTime(seconds)
  const minutes = Math.floor(normalized / 60).toString().padStart(2, '0')
  const remainder = (normalized % 60).toString().padStart(2, '0')
  return `${minutes}:${remainder}`
}

export function useAudioPlaybackTime(
  audioUrl: string | undefined,
  fallbackDurationSec = 0,
) {
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const [currentSec, setCurrentSec] = useState(0)
  const [metadataDurationSec, setMetadataDurationSec] = useState(0)
  const [isPlaying, setIsPlaying] = useState(false)
  const durationSec = metadataDurationSec || normalizeDuration(fallbackDurationSec)

  useEffect(() => {
    setCurrentSec(0)
    setMetadataDurationSec(0)
    setIsPlaying(false)
  }, [audioUrl])

  const handleLoadedMetadata = useCallback(() => {
    const duration = audioRef.current?.duration
    if (duration != null && Number.isFinite(duration)) {
      setMetadataDurationSec(normalizeDuration(duration))
    }
  }, [])

  const handleTimeUpdate = useCallback(() => {
    setCurrentSec(normalizeCurrentTime(audioRef.current?.currentTime ?? 0))
  }, [])

  const handlePlay = useCallback(() => setIsPlaying(true), [])
  const handlePause = useCallback(() => setIsPlaying(false), [])
  const handleEnded = useCallback(() => {
    setCurrentSec(durationSec)
    setIsPlaying(false)
  }, [durationSec])

  const togglePlayback = useCallback(async () => {
    const audio = audioRef.current
    if (!audio) return

    if (audio.paused) {
      try {
        await audio.play()
      } catch {
        setIsPlaying(false)
      }
      return
    }

    audio.pause()
  }, [])

  const resetPlayback = useCallback(() => {
    const audio = audioRef.current
    if (audio) {
      audio.pause()
      audio.currentTime = 0
    }
    setCurrentSec(0)
    setIsPlaying(false)
  }, [])

  return {
    audioRef,
    currentSec,
    durationSec,
    isPlaying,
    handleLoadedMetadata,
    handleTimeUpdate,
    handlePlay,
    handlePause,
    handleEnded,
    togglePlayback,
    resetPlayback,
  }
}
